import React, { useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, MessageCircle, Loader2, Send, Clock } from "lucide-react";
import { toast } from "sonner";
import { COMPANY, SERVICES } from "@/data/site";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast.success("Thank you! Your inquiry has been received. We'll be in touch shortly.");
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try WhatsApp or call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  const waMessage = encodeURIComponent(
    "Hello Hasan Javed Glass & Aluminum, I'd like to request a quote."
  );
  const waLink = `https://wa.me/${COMPANY.whatsapp}?text=${waMessage}`;

  return (
    <section id="contact" data-testid="contact-section" className="bg-white">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-14 py-24 md:py-32">
        <div className="grid lg:grid-cols-12 gap-14 lg:gap-16">
          {/* Left: info */}
          <div className="lg:col-span-5">
            <div className="overline text-blue-600 mb-6">Get in touch</div>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-light tracking-tighter text-slate-950 text-balance">
              Let&apos;s discuss your next project.
            </h2>
            <p className="mt-6 text-slate-600 text-base md:text-lg leading-relaxed">
              Send us your project requirements and we&apos;ll respond with a detailed, no-obligation
              quotation within one business day.
            </p>

            <div className="mt-12 space-y-6">
              <a
                href={`tel:${COMPANY.phoneRaw}`}
                data-testid="contact-phone"
                className="flex items-start gap-4 group"
              >
                <div className="h-11 w-11 border border-slate-300 group-hover:border-slate-900 flex items-center justify-center transition-colors">
                  <Phone className="h-4 w-4 text-slate-800" strokeWidth={1.5} />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500">Phone</div>
                  <div className="font-display text-lg text-slate-950 mt-1 group-hover:text-blue-600 transition-colors">
                    {COMPANY.phone}
                  </div>
                </div>
              </a>

              <a
                href={`mailto:${COMPANY.email}`}
                data-testid="contact-email"
                className="flex items-start gap-4 group"
              >
                <div className="h-11 w-11 border border-slate-300 group-hover:border-slate-900 flex items-center justify-center transition-colors">
                  <Mail className="h-4 w-4 text-slate-800" strokeWidth={1.5} />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500">Email</div>
                  <div className="font-display text-lg text-slate-950 mt-1 group-hover:text-blue-600 transition-colors break-all">
                    {COMPANY.email}
                  </div>
                </div>
              </a>

              <div className="flex items-start gap-4" data-testid="contact-location">
                <div className="h-11 w-11 border border-slate-300 flex items-center justify-center">
                  <MapPin className="h-4 w-4 text-slate-800" strokeWidth={1.5} />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500">Location</div>
                  <div className="font-display text-lg text-slate-950 mt-1">{COMPANY.location}</div>
                </div>
              </div>

              <div className="flex items-start gap-4" data-testid="contact-hours">
                <div className="h-11 w-11 border border-slate-300 flex items-center justify-center">
                  <Clock className="h-4 w-4 text-slate-800" strokeWidth={1.5} />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500">Business Hours</div>
                  <div className="font-display text-lg text-slate-950 mt-1">{COMPANY.hours}</div>
                </div>
              </div>
            </div>

            <a
              href={waLink}
              target="_blank"
              rel="noreferrer"
              data-testid="contact-whatsapp-cta"
              className="mt-12 inline-flex items-center gap-3 bg-[#25D366] hover:bg-[#20BD5A] text-white px-8 py-4 text-xs uppercase tracking-[0.22em] font-semibold transition-colors"
            >
              <MessageCircle className="h-5 w-5" />
              Chat on WhatsApp
            </a>

            {/* Map */}
            <div className="mt-12 border border-slate-200 overflow-hidden" data-testid="contact-map">
              <iframe
                title="Hasan Javed Glass & Aluminum — Location"
                src={`https://maps.google.com/maps?q=${COMPANY.mapsQuery}&t=&z=17&ie=UTF8&iwloc=&output=embed`}
                width="100%"
                height="260"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
              <a
                href={COMPANY.mapsLink}
                target="_blank"
                rel="noreferrer"
                data-testid="contact-directions"
                className="block bg-slate-900 text-white text-xs uppercase tracking-[0.22em] font-semibold text-center py-4 hover:bg-slate-800 transition-colors"
              >
                Get Directions →
              </a>
            </div>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-7">
            <form
              onSubmit={submit}
              data-testid="contact-form"
              className="bg-slate-50 border border-slate-200 p-8 md:p-12"
            >
              <h3 className="font-display text-2xl font-medium text-slate-950">Request a free quote</h3>
              <p className="mt-2 text-slate-600 text-sm">
                Fields marked with <span className="text-blue-600">*</span> are required.
              </p>

              <div className="mt-8 grid md:grid-cols-2 gap-6">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold">
                    Full name <span className="text-blue-600">*</span>
                  </label>
                  <input
                    data-testid="form-name"
                    type="text"
                    value={form.name}
                    onChange={set("name")}
                    required
                    className="mt-3 w-full border-b border-slate-300 bg-transparent py-3 focus:border-slate-900 focus:outline-none text-slate-950"
                    placeholder="Your name"
                  />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold">
                    Phone <span className="text-blue-600">*</span>
                  </label>
                  <input
                    data-testid="form-phone"
                    type="tel"
                    value={form.phone}
                    onChange={set("phone")}
                    required
                    className="mt-3 w-full border-b border-slate-300 bg-transparent py-3 focus:border-slate-900 focus:outline-none text-slate-950"
                    placeholder="+971 ..."
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold">
                    Email <span className="text-blue-600">*</span>
                  </label>
                  <input
                    data-testid="form-email"
                    type="email"
                    value={form.email}
                    onChange={set("email")}
                    required
                    className="mt-3 w-full border-b border-slate-300 bg-transparent py-3 focus:border-slate-900 focus:outline-none text-slate-950"
                    placeholder="you@company.com"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold">
                    Service of interest
                  </label>
                  <select
                    data-testid="form-service"
                    value={form.service}
                    onChange={set("service")}
                    className="mt-3 w-full border-b border-slate-300 bg-transparent py-3 focus:border-slate-900 focus:outline-none text-slate-950"
                  >
                    <option value="">Select a service (optional)</option>
                    {SERVICES.map((s) => (
                      <option key={s.name} value={s.name}>{s.name}</option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-semibold">
                    Project details <span className="text-blue-600">*</span>
                  </label>
                  <textarea
                    data-testid="form-message"
                    rows={5}
                    value={form.message}
                    onChange={set("message")}
                    required
                    className="mt-3 w-full border-b border-slate-300 bg-transparent py-3 focus:border-slate-900 focus:outline-none text-slate-950 resize-none"
                    placeholder="Tell us about the project, size, timeline..."
                  />
                </div>
              </div>

              <div className="mt-10 flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
                <p className="text-xs text-slate-500 max-w-sm">
                  By submitting, you agree to be contacted regarding your inquiry. We never share your details.
                </p>
                <button
                  type="submit"
                  data-testid="form-submit"
                  disabled={submitting}
                  className="bg-blue-600 hover:bg-blue-700 disabled:opacity-70 text-white px-8 py-4 text-xs uppercase tracking-[0.22em] font-semibold flex items-center justify-center gap-3 transition-colors"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> Sending
                    </>
                  ) : (
                    <>
                      Send Inquiry <Send className="h-4 w-4" />
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};
